<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { NButton, NSpin } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import AdminReminderList from './AdminReminderList.vue'
import AdminScheduleList from './AdminScheduleList.vue'
import ReminderSegmented from './ReminderSegmented.vue'
import { useRemindersStore } from '../stores/reminders'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// The Reminders page: one card with a One-time / Schedules segmented
// control. The one-time and recurring halves live in their own components
// (#1092); this view owns the loading/error state, the segment, and the
// header's "New reminder" action, which focuses the active composer.

const store = useRemindersStore()
useDocumentTitle('Reminders')

type Segment = 'schedules' | 'one-time'

const tab = ref<Segment>('one-time')
const TYPE_OPTIONS = [
  { label: 'One-time', value: 'one-time' },
  { label: 'Schedules', value: 'schedules' },
]

const reminderList = ref<InstanceType<typeof AdminReminderList> | null>(null)
const scheduleList = ref<InstanceType<typeof AdminScheduleList> | null>(null)

async function newReminder(): Promise<void> {
  await nextTick()
  if (tab.value === 'schedules') await scheduleList.value?.focusForm()
  else await reminderList.value?.focusForm()
}

onMounted(async () => {
  try {
    await store.fetchAll('pending')
  } catch {
    // loadError surfaces in the template
  }
})
</script>

<template>
  <RouteShell>
    <section class="mx-auto flex max-w-3xl flex-col gap-6 pb-6">
      <header class="flex flex-wrap items-end gap-3">
        <div class="min-w-[240px] flex-1">
          <h1 class="page-title">Reminders</h1>
          <p class="m-0 mt-1.5 text-ink-muted">Reminders reach you on your channels — once, or on a schedule.</p>
        </div>
        <button
          type="button"
          class="min-h-11 rounded-[10px] bg-life px-4 font-semibold text-surface-bg hover:opacity-90 sm:min-h-10"
          data-testid="reminders-new"
          @click="newReminder"
        >
          New reminder
        </button>
      </header>

      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <NSpin aria-label="Loading…" />
      </div>
      <div v-else-if="store.loadError" class="flex flex-col items-center gap-3 py-12">
        <p class="m-0 text-sm text-status-alert">{{ store.loadError }}</p>
        <NButton size="small" @click="store.fetchAll()">Try again</NButton>
      </div>

      <section
        v-else
        aria-labelledby="reminders-heading"
        class="flex flex-col gap-2 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
      >
        <div class="flex flex-wrap items-center gap-2 pb-1">
          <span class="h-2 w-2 rounded-full bg-life" aria-hidden="true" />
          <h2 id="reminders-heading" class="m-0 flex-1 text-[15px] font-semibold text-ink">Reminders</h2>
          <ReminderSegmented
            :model-value="tab"
            :options="TYPE_OPTIONS"
            label="Reminder type"
            controls="reminders-panel"
            @update:model-value="(v: string) => (tab = v as Segment)"
          />
        </div>
        <div id="reminders-panel" role="tabpanel" aria-labelledby="reminders-heading">
          <AdminReminderList v-if="tab === 'one-time'" ref="reminderList" />
          <AdminScheduleList v-else ref="scheduleList" />
        </div>
      </section>
    </section>
  </RouteShell>
</template>
